import api from "@/lib/api";

/** Convert a base64url VAPID key into the Uint8Array format the Push API
 * expects for `applicationServerKey`. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function currentPermission() {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

async function getRegistration() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;
  return navigator.serviceWorker.ready;
}

export async function isPushSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/** End-to-end subscribe flow. Returns:
 *   { ok: true }                          — subscribed successfully
 *   { ok: false, reason: 'permission' }   — user denied browser permission
 *   { ok: false, reason: 'unsupported' }  — browser lacks push APIs
 *   { ok: false, reason: 'no-key' }       — server not configured
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "permission" };

  // Fetch the VAPID public key from our backend
  let publicKey;
  try {
    const { data } = await api.get("/push/vapid-key");
    publicKey = data?.public_key;
  } catch {
    return { ok: false, reason: "no-key" };
  }
  if (!publicKey) return { ok: false, reason: "no-key" };

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const raw = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent,
  });
  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      try { await api.post("/push/unsubscribe", { endpoint }); } catch {}
    }
  } catch {}
}
